import { Theme } from "@material-ui/core/styles";

// ----------------------------------------------------------------------

/**
 * ── The RTL arrow fix ────────────────────────────────────────────────────────
 *
 * Tooltip arrows were offset in RTL — and ONLY in RTL — on every tooltip in the
 * app that uses `arrow`. It is not a per-component problem, so it is fixed here.
 *
 * The cause is a collision between two things that are each correct on their own:
 *
 *  1. Popper positions a top/bottom arrow with INLINE styles — `left: 0` plus
 *     `transform: translate(Xpx, 0)`. (Verified in @popperjs `computeStyles`: the
 *     arrow is mapped with `adaptive: false`, so `sideX` is always `left`, and
 *     with gpuAcceleration on it emits `left: '0'` and puts the offset in the
 *     transform.)
 *  2. MUI's own stylesheet ALSO declares `left: 0` on the arrow for top and
 *     bottom placements — a harmless duplicate of what popper writes inline.
 *
 * `stylis-plugin-rtl` rewrites the STYLESHEET and cannot see inline styles, so in
 * RTL (2) becomes `right: 0` while (1) stays `left: 0`. The arrow is now
 * over-constrained — `left`, `width` and `right` all non-auto — and CSS resolves
 * that by ignoring one of them based on the containing block's direction: `right`
 * loses in LTR, `left` loses in RTL. So in RTL the arrow is pinned to the far edge
 * of the tooltip and then translated by an offset that assumed a left origin,
 * landing it in the rounded corner looking like a detached notch.
 *
 * Measured flips (stylis-plugin-rtl over MUI's own arrow rules):
 *
 *     top/bottom  left:0                    ->  right:0
 *     right       left:0;  margin-left:-…   ->  right:0; margin-right:-…
 *     left        right:0; margin-right:-…  ->  left:0;  margin-left:-…
 *     ::before    transform-origin          ->  unchanged
 *
 * The repairs below are therefore two different shapes:
 *
 *  - TOP/BOTTOM: the class must not name a horizontal edge at all, so popper's
 *    inline `left` is free to place it. `left: auto` + `right: auto` is
 *    FLIP-INVARIANT (stylis just swaps one `auto` for the other), which is why it
 *    needs no direction branch and cannot rot if the RTL setup changes.
 *  - LEFT/RIGHT: here the class IS the horizontal anchor — popper only supplies
 *    `top` + `translateY` — so a real edge has to survive. These values are
 *    written PRE-MIRRORED so that stylis' flip lands on the physically correct
 *    edge, and they are applied only in RTL, where that flip actually happens.
 *
 * If MUI is ever upgraded to a version that makes its arrow rules RTL-aware
 * itself, the left/right half of this becomes a double-flip and must be deleted.
 * The top/bottom half stays correct either way.
 */
export default function Tooltip(theme: Theme) {
  const isLight = theme.palette.mode === "light";
  const isRtl = theme.direction === "rtl";

  const arrow = ".MuiTooltip-arrow";

  return {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.grey[isLight ? 800 : 700]
        },
        arrow: {
          color: theme.palette.grey[isLight ? 800 : 700]
        },
        popper: {
          // Flip-invariant, so it is applied in both directions: in LTR it is a
          // no-op (popper's inline `left` already won), in RTL it is the fix.
          [`&[data-popper-placement*="top"] ${arrow}, &[data-popper-placement*="bottom"] ${arrow}`]:
            {
              left: "auto",
              right: "auto"
            },
          ...(isRtl && {
            // Tooltip sits to the RIGHT of the anchor, so the arrow belongs on the
            // tooltip's LEFT edge. Written as `right` so the flip produces `left`.
            [`&[data-popper-placement*="right"] ${arrow}`]: {
              right: 0,
              marginRight: "-0.71em"
            },
            // Mirror image of the above.
            [`&[data-popper-placement*="left"] ${arrow}`]: {
              left: 0,
              marginLeft: "-0.71em"
            }
          })
        }
      }
    }
  };
}
